import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { gradingService } from '@/services/gradingService'
import type { GradingComponent } from '@/types/grading'

export function useGradingScheme(courseId: string) {
  return useQuery({
    queryKey: ['grading-scheme', courseId],
    queryFn: () => gradingService.getGradingScheme(courseId),
    enabled: !!courseId,
  })
}

export function useFinalGrade(enrollmentId: string) {
  return useQuery({
    queryKey: ['final-grade', enrollmentId],
    queryFn: () => gradingService.getFinalGrade(enrollmentId),
    enabled: !!enrollmentId,
  })
}

export function usePracticalScores(enrollmentId: string) {
  return useQuery({
    queryKey: ['practical-scores', enrollmentId],
    queryFn: () => gradingService.getPracticalScores(enrollmentId),
    enabled: !!enrollmentId,
  })
}

export function usePracticalScoresBatch(enrollmentIds: string[]) {
  return useQuery({
    queryKey: ['practical-scores-batch', enrollmentIds.sort()],
    queryFn: () => gradingService.getPracticalScoresBatch(enrollmentIds),
    enabled: enrollmentIds.length > 0,
  })
}

export function useQuizScores(enrollmentId: string) {
  return useQuery({
    queryKey: ['enrollment-quiz-scores', enrollmentId],
    queryFn: () => gradingService.getQuizScores(enrollmentId),
    enabled: !!enrollmentId,
  })
}

export function useCreateGradingScheme() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ courseId, components }: { courseId: string; components: GradingComponent[] }) =>
      gradingService.createGradingScheme(courseId, components),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: ['grading-scheme', courseId] })
    },
  })
}

/** useSetGradingScheme — updates an existing scheme (PUT /grading/scheme/:course_id) */
export function useSetGradingScheme() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ courseId, components }: { courseId: string; components: GradingComponent[] }) =>
      gradingService.updateGradingScheme(courseId, components),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: ['grading-scheme', courseId] })
    },
  })
}

export function useRecordPracticalScore() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      enrollmentId,
      data,
    }: {
      enrollmentId: string
      data: {
        trainerId?: string
        score: number
        notes?: string
        attachmentUrl?: string
      }
    }) =>
      gradingService.recordPracticalScore(enrollmentId, {
        trainerId: data.trainerId || '',
        score: data.score,
        notes: data.notes,
      }),
    onSuccess: (_, { enrollmentId }) => {
      queryClient.invalidateQueries({ queryKey: ['practical-scores', enrollmentId] })
      queryClient.invalidateQueries({ queryKey: ['final-grade', enrollmentId] })
    },
  })
}

export function useCalculateFinalGrade() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (enrollmentId: string) => gradingService.calculateFinalGrade(enrollmentId),
    onSuccess: (_, enrollmentId) => {
      queryClient.invalidateQueries({ queryKey: ['final-grade', enrollmentId] })
    },
  })
}
